/**
 * Turning a message's attachments into what a bubble draws.
 *
 * A message can carry several — four pictures picked at once travel as ONE
 * message — so these two helpers are what keep the extras from being dropped
 * on the way to the screen.
 */

import type { MessageMedia } from "./types"

/**
 * How many files one message may carry.
 *
 * Enforced where they are chosen, so the limit is met while picking rather
 * than after a long upload. Five also keeps the album a shape a bubble can
 * hold — four tiles and a count.
 */
export const MAX_ATTACHMENTS_PER_MESSAGE = 5

/**
 * The messages a set of picked files goes out as, the way WhatsApp sends them:
 * photos together as one album, every other file as a message of its own,
 * in the order picked.
 *
 * Everything used to go as ONE message, and a bubble draws a single video,
 * document or audio file — so of three documents sent together the recipient
 * saw the first, and the other two were stored and never shown. One message
 * per file also gives each its own bubble and progress while it uploads.
 */
export function groupIntoMessages<T extends { kind?: string }>(files: T[]): T[][] {
  const photos = files.filter((f) => f.kind === "photo")
  const others = files.filter((f) => f.kind !== "photo")
  return [...(photos.length > 0 ? [photos] : []), ...others.map((f) => [f])]
}

/** One attachment as it arrives on the wire. */
export interface WireLikeAttachment {
  url: string
  kind?: string | null
  mime?: string
  filename?: string | null
  sizeBytes?: number | null
  thumbnailUrl?: string | null
  width?: number | null
  height?: number | null
  durationMs?: number | null
}

/** Flatten every attachment for the UI, in send order. */
export function toMessageMedia(
  attachments: WireLikeAttachment[] | undefined,
  /** The caller's own mime → kind rule; only consulted for rows written
   *  before kinds existed. */
  kindFromMime: (mime: string) => string,
): MessageMedia[] | undefined {
  if (!attachments) return undefined
  return attachments.map((a) => ({
    url: a.url,
    name: a.filename ?? undefined,
    size: a.sizeBytes ?? undefined,
    mime: a.mime,
    thumbnailUrl: a.thumbnailUrl ?? undefined,
    width: a.width,
    height: a.height,
    kind: a.kind ?? kindFromMime(a.mime ?? ""),
  }))
}

/**
 * How many tiles an album shows, how many it only counts, and the shape they
 * are arranged in.
 *
 * Past [max] the last tile carries a "+N" rather than the grid growing, so a
 * message with twenty pictures stays the size of a message. Three is its own
 * shape: dropping three into a 2x2 leaves a hole where the fourth would be,
 * so the first picture takes the full height beside the other two.
 */
export function albumLayout(
  count: number,
  max = 4,
): { shown: number; hidden: number; shape: 'single' | 'pair' | 'feature' | 'quad' } {
  const shown = Math.max(0, Math.min(count, max))
  const shape =
    shown <= 1 ? 'single' : shown === 2 ? 'pair' : shown === 3 ? 'feature' : 'quad'
  return { shown, hidden: Math.max(0, count - shown), shape }
}
